import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import Button from "../Buttons/Button";
import DropdownMenu from "../Buttons/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useTabsURL } from "./NewTabWrapper";

const NewTab = ({
  tabs,
  mainTabsShow = 0,
  defaultTab = 0,
  color = "primary",
  activeColor = "primary",
  variant = "contained",
  activeVariant = "contained",
  onTabChange, // new
  ///////////////////////////////
  mode = "basic", //basic, url
  tabId = "tabId",
}) => {
  const navigate = useNavigate();
  const tabContainerRef = useRef(null);
  const tabWidthsRef = useRef([]);
  const [visibleTabs, setVisibleTabs] = useState(tabs.length);

  const tabsURL = useTabsURL();
  const findMainTabs = useMemo(
    () => tabs.filter((tab) => tab.type === "main"),
    [tabs],
  );
  const urlMode = useMemo(() => {
    const v = import.meta.env && import.meta.env.VITE_PAGE_TAB_URL;
    return String(v || "").toLowerCase();
  }, []);
  const writeSlug = useMemo(() => {
    return ["readable", "non-readable", "encoded", "opaque"].includes(urlMode);
  }, [urlMode]);
  const makeSlug = useCallback((s) => {
    if (typeof s !== "string") return "";
    return s
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }, []);
  const initialActiveTab = useMemo(() => {
    if (mode === "url" && tabsURL) {
      const v = tabsURL.getParam(tabId);
      if (v !== null && v !== undefined && v !== "") {
        const num = Number(v);
        if (!Number.isNaN(num)) return num;
        const mainOnly = tabs.filter((t) => t.type === "main");
        const idx = mainOnly.findIndex(
          (t) => makeSlug(t.slug ?? t.title) === String(v),
        );
        return idx >= 0 ? idx : defaultTab;
      }
      return defaultTab;
    }
    return defaultTab;
  }, [mode, tabId, tabsURL, defaultTab, tabs, makeSlug]);
  const [activeMainTab, setActiveMainTab] = useState(initialActiveTab);
  const [activeSubTab, setActiveSubTab] = useState(null);

  const calculateVisibleTabs = useCallback(() => {
    if (!tabContainerRef.current) return;

    const container = tabContainerRef.current;
    const containerWidth = container.offsetWidth;
    const availableWidth = containerWidth - 100;

    const tabElements = container.querySelectorAll(".main-tab-button");
    if (tabElements.length && tabWidthsRef.current.length === 0) {
      tabWidthsRef.current = Array.from(tabElements).map(
        (tab) => tab.offsetWidth + 8,
      );
    }

    const widths = tabWidthsRef.current;
    if (!widths.length) {
      setVisibleTabs(findMainTabs.length);
      return;
    }

    let totalWidth = 0;
    let visibleCount = widths.length;

    for (let i = 0; i < widths.length; i += 1) {
      totalWidth += widths[i];
      if (totalWidth > availableWidth) {
        visibleCount = i;
        break;
      }
    }

    setVisibleTabs(visibleCount);
  }, [findMainTabs]);

  useEffect(() => {
    tabWidthsRef.current = [];
    setVisibleTabs(findMainTabs.length);
  }, [findMainTabs.length]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(calculateVisibleTabs);
    if (tabContainerRef.current) {
      resizeObserver.observe(tabContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [calculateVisibleTabs]);

  useEffect(() => {
    if (mode !== "url" || !tabsURL) return;
    const current = tabsURL.getParam(tabId);
    if (current === null || current === undefined || current === "") {
      const mainOnly = tabs.filter((t) => t.type === "main");
      const item = mainOnly[initialActiveTab];
      const slug = item
        ? makeSlug(item.slug ?? item.title)
        : String(initialActiveTab);
      const value = writeSlug ? slug : String(initialActiveTab);
      tabsURL.setParam(tabId, value);
    }
  }, [mode, tabsURL, tabId, initialActiveTab, tabs, makeSlug, writeSlug]);

  useEffect(() => {
    if (mode !== "url" || !tabsURL) return;
    tabsURL.registerKey(tabId);
    const unsub = tabsURL.subscribe(tabId, (value) => {
      if (value !== null && value !== undefined && value !== "") {
        const val = String(value);
        if (writeSlug) {
          const mainOnly = tabs.filter((t) => t.type === "main");
          const idx = mainOnly.findIndex(
            (t) => makeSlug(t.slug ?? t.title) === val,
          );
          if (idx >= 0) {
            setActiveMainTab(idx);
            setActiveSubTab(null);
            return;
          }
          const allSubs = tabs.filter((t) => t.type === "sub");
          const sIdx = allSubs.findIndex(
            (t) => t.likeMain && makeSlug(t.slug ?? t.title) === val,
          );
          if (sIdx >= 0) {
            setActiveSubTab(sIdx);
          }
          return;
        }
        const num = Number(val);
        if (!Number.isNaN(num)) {
          setActiveMainTab(num);
          setActiveSubTab(null);
          return;
        }
        const mainOnly = tabs.filter((t) => t.type === "main");
        const idx = mainOnly.findIndex(
          (t) => makeSlug(t.slug ?? t.title) === val,
        );
        if (idx >= 0) {
          setActiveMainTab(idx);
          setActiveSubTab(null);
          return;
        }
        const allSubs = tabs.filter((t) => t.type === "sub");
        const sIdx = allSubs.findIndex(
          (t) => t.likeMain && makeSlug(t.slug ?? t.title) === val,
        );
        if (sIdx >= 0) {
          setActiveSubTab(sIdx);
        }
      }
    });
    const current = tabsURL.getParam(tabId);
    if (current !== null && current !== undefined && current !== "") {
      const val = String(current);
      if (writeSlug) {
        const mainOnly = tabs.filter((t) => t.type === "main");
        const idx = mainOnly.findIndex(
          (t) => makeSlug(t.slug ?? t.title) === val,
        );
        if (idx >= 0) {
          setActiveMainTab(idx);
          setActiveSubTab(null);
        } else {
          const allSubs = tabs.filter((t) => t.type === "sub");
          const sIdx = allSubs.findIndex(
            (t) => t.likeMain && makeSlug(t.slug ?? t.title) === val,
          );
          if (sIdx >= 0) {
            setActiveSubTab(sIdx);
          }
        }
      } else {
        const num = Number(val);
        if (!Number.isNaN(num)) {
          setActiveMainTab(num);
          setActiveSubTab(null);
        } else {
          const mainOnly = tabs.filter((t) => t.type === "main");
          const idx = mainOnly.findIndex(
            (t) => makeSlug(t.slug ?? t.title) === val,
          );
          if (idx >= 0) {
            setActiveMainTab(idx);
            setActiveSubTab(null);
          } else {
            const allSubs = tabs.filter((t) => t.type === "sub");
            const sIdx = allSubs.findIndex(
              (t) => t.likeMain && makeSlug(t.slug ?? t.title) === val,
            );
            if (sIdx >= 0) {
              setActiveSubTab(sIdx);
            }
          }
        }
      }
    }
    return () => {
      unsub();
      tabsURL.unregisterKey(tabId);
    };
  }, [mode, tabId, tabsURL, tabs, makeSlug, writeSlug]);

  // Modify the useMemo to use visibleTabs
  const { mainTabs, mainMore, subTabs, showMore } = useMemo(() => {
    const effectiveTabCount =
      mainTabsShow === 0 ? visibleTabs : Math.min(mainTabsShow, visibleTabs);

    const baseVisible = findMainTabs
      .slice(0, effectiveTabCount)
      .map((item, index) => ({ item, index }));
    const baseMore = findMainTabs
      .slice(effectiveTabCount)
      .map((item, i) => ({ item, index: effectiveTabCount + i }));

    let mainTabs = baseVisible;
    let mainMore = baseMore;

    const activeIsInMore = activeMainTab >= effectiveTabCount;
    if (activeIsInMore && baseMore.length > 0 && baseVisible.length > 0) {
      const activeEntry = baseMore.find((t) => t.index === activeMainTab);
      if (activeEntry) {
        const displaced = baseVisible[baseVisible.length - 1];
        mainTabs = [...baseVisible.slice(0, -1), activeEntry];
        mainMore = [
          displaced,
          ...baseMore.filter((t) => t.index !== activeMainTab),
        ];
      }
    }

    const activeMainTabData = findMainTabs[activeMainTab];
    const subTabs =
      activeMainTabData?.subTabs ||
      tabs.filter((tab) => tab.type === "sub") ||
      [];

    const showMore = mainMore.length > 0;

    return { mainTabs, mainMore, subTabs, showMore };
  }, [tabs, mainTabsShow, visibleTabs, activeMainTab, findMainTabs]);

  const isMoreMenuActive = useMemo(() => {
    return mainMore.some((t) => t.index === activeMainTab);
  }, [mainMore, activeMainTab]);

  const handleTabClick = useCallback(
    (item, index, isMainTab = true) => {
      if (isMainTab) {
        if (mode === "url" && tabsURL) {
          const slug = makeSlug((item && item.slug) ?? item.title);
          const value = writeSlug ? slug : String(index);
          tabsURL.setParam(tabId, value);
        }
        setActiveMainTab(index);
        setActiveSubTab(null);
        if (onTabChange) {
          onTabChange(index);
        }
      } else {
        if (item.likeMain && mode === "url" && tabsURL) {
          const slug = makeSlug((item && item.slug) ?? item.title);
          tabsURL.setParam(tabId, slug);
        }
        setActiveSubTab(index);
      }
      if (item.action) {
        item.action();
      }
      if (item.doubleAction) {
        item.doubleAction();
      }
      if (item.link) {
        navigate(item.link);
      }
    },
    [navigate, onTabChange, mode, tabsURL, tabId, writeSlug, makeSlug],
  );

  return (
    <div
      role="tablist"
      className="tab-container flex flex-1 flex-col overflow-hidden"
    >
      <div className="flex justify-between items-end gap-10 border-b-[1px]  border-gray-200">
        <div ref={tabContainerRef} className="flex gap-2 items-end flex-1">
          {/* Main tabs remain the same */}
          {mainTabs.map(({ item, index }) => (
            <Button
              key={index}
              className="main-tab-button"
              type="tab"
              role="tab"
              aria-selected={index === activeMainTab}
              color={index === activeMainTab ? activeColor : color}
              variant={index === activeMainTab ? activeVariant : variant}
              title={item.title}
              icon={item.icon && item.icon}
              iconPosition={item.iconPosition && item.iconPosition}
              size="sm"
              onClick={() => handleTabClick(item, index)}
              active={
                index === activeMainTab &&
                (activeColor === color || variant === activeVariant)
              }
              disabled={item.disabled}
              value={item.value && item.value}
            />
          ))}

          {showMore && (
            <DropdownMenu
              MenuSection={
                <div className="p-1 flex flex-col gap-0.5 max-h-[250px] overflow-y-auto">
                  {mainMore.map(({ item, index }) => (
                    <div key={index} className="w-full">
                      <Button
                        title={item.title}
                        icon={item.icon}
                        variant="text"
                        color={index === activeMainTab ? activeColor : color}
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          handleTabClick(item, index);
                        }}
                        active={index === activeMainTab}
                        fromLeft
                      />
                    </div>
                  ))}
                </div>
              }
            >
              <Button
                type="tab"
                size="sm"
                title={`More`}
                color={isMoreMenuActive ? activeColor : color}
                variant={isMoreMenuActive ? activeVariant : variant}
                icon="keyboard_arrow_down"
                iconPosition="after"
              />
            </DropdownMenu>
          )}
        </div>

        <div className="sub-tabs flex gap-2 items-end">
          {subTabs.map((item, key) => (
            <Button
              key={key}
              type="tab"
              role="tab"
              aria-selected={key === activeSubTab}
              color={`${variant === "text" ? "primary" : "black"}`}
              title={item.title}
              icon={item.icon}
              size="sm"
              onClick={() => handleTabClick(item, key, false)}
              variant={variant === "text" ? "text" : "contained"}
              active={key === activeSubTab}
            />
          ))}
        </div>
      </div>

      <div role="tabpanel" className="tab-content flex flex-1 overflow-hidden">
        {activeSubTab !== null && subTabs[activeSubTab]?.likeMain ? (
          <div className="flex flex-1 flex-col overflow-auto">
            {subTabs[activeSubTab].page}
          </div>
        ) : (
          findMainTabs[activeMainTab]?.page && (
            <div className="flex flex-1 flex-col overflow-auto">
              {React.cloneElement(findMainTabs[activeMainTab].page, {
                setActiveTab: (next) => {
                  const nextIndex =
                    typeof next === "function" ? next(activeMainTab) : next;
                  const safeIndex = Math.max(
                    0,
                    Math.min(findMainTabs.length - 1, Number(nextIndex) || 0),
                  );
                  if (mode === "url" && tabsURL) {
                    const item = findMainTabs[safeIndex];
                    const slug = item
                      ? makeSlug(item.slug ?? item.title)
                      : String(safeIndex);
                    const value = writeSlug ? slug : String(safeIndex);
                    tabsURL.setParam(tabId, value);
                  }
                  setActiveMainTab(safeIndex);
                  setActiveSubTab(null);
                  if (onTabChange) onTabChange(safeIndex);
                },
              })}
            </div>
          )
        )}
        {activeSubTab !== null &&
          subTabs[activeSubTab]?.page &&
          !subTabs[activeSubTab]?.likeMain && (
            <div>{subTabs[activeSubTab].page}</div>
          )}
      </div>
    </div>
  );
};

export default NewTab;
